import { motion, useInView } from "framer-motion";
import { graphql, Link } from "gatsby";
import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { Autoplay, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import ImageOpt from "./ImageOpt";
import { v4 as uuidv4 } from 'uuid';

const SliderWithContent = ({ module }) => {
  const ref = useRef(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const isInView = useInView(ref);
  const swiperRef = useRef(null);
  const uniqueId = uuidv4();
  
  // Navigation refs
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);

  const sectionShow = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  };

  const animFade = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  };

  useEffect(() => {
    if (isInView && !hasPlayed) {
      setHasPlayed(true);
    }
  }, [isInView, hasPlayed]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  }, [module.images]);

  if (module.hideSection === "no") {
    return (
      <motion.section
        initial="hidden"
        animate={isInView || hasPlayed ? "show" : "hidden"}
        variants={sectionShow}
        className={`zigzag-slider-content slider-${uniqueId} py-70 lgscreen:py-60 mdscreen:py-45 ${
          module.extraClass ? ` ${module.extraClass}` : ""
        }`}
        id={module.extraId}
      >
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView || hasPlayed ? "show" : "hidden"}
          variants={sectionShow}
          className={`container-fluid-md2 ${
            module.imagePosition === "left" ? "lg:!pl-30" : "lg:!pr-30"
          }`}
        >
          <div className="flex flex-wrap items-center">
            <motion.div
              variants={animFade}
              className={`lgscreen:w-full w-6/12 ${
                module.imagePosition === "left"
                  ? "order-1 lg:order-1"
                  : "order-1 lg:order-2"
              }`}
            >
              {module?.images && (
                <div className="w-full">
                  <div className="slider-main border-bg-offwhite relative">
                    <Swiper
                      slidesPerView={1}
                      speed={1000}
                      centeredSlides={true}
                      modules={[Autoplay, Navigation]}
                      navigation={{
                        prevEl: prevButtonRef.current,
                        nextEl: nextButtonRef.current,
                        disabledClass: "btn-disabled",
                      }}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                      onInit={(swiper) => {
                        // Override prevEl & nextEl references after init
                        swiper.params.navigation.prevEl = prevButtonRef.current;
                        swiper.params.navigation.nextEl = nextButtonRef.current;
                        swiper.navigation.init();
                        swiper.navigation.update();
                      }}
                    >
                      {module?.images.map((item, i) => (
                        <SwiperSlide key={i}>
                          {item?.image && (
                            <div className="img">
                              <ImageOpt
                                imgData={item.image.mediaItemUrl}
                                imgAlt={item.image.title}
                                imgClass=""
                                width={2000}
                                height={1000}
                                imgLoad="lazy"
                              />
                            </div>
                          )}
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="swiper-arrow flex justify-end gap-x-25 absolute bottom-30 right-45 w-full z-9 lgscreen:right-30 lgscreen:bottom-20">
                      <button
                        ref={prevButtonRef}
                        className={`swiper-btn w-[30px] h-[30px] lgscreen:w-[20px] lgscreen:h-[20px] rounded-100 bg-opacity-10 border-1 border-white-100 flex flex-wrap justify-center items-center transition-common`}
                        aria-label="Previous slide"
                      >
                        <img
                          src="../../images/swiper-arrowPrev-white.svg"
                          width={15}
                          height={15}
                          loading="lazy"
                          alt="Arrow"
                          className="lgscreen:h-[10px] lgscreen:w-[10px]"
                        />
                      </button>
                      <button
                        ref={nextButtonRef}
                        className={`swiper-btn w-[30px] h-[30px] lgscreen:w-[20px] lgscreen:h-[20px] rounded-100 bg-opacity-10 border-1 border-white-100 flex flex-wrap justify-center items-center transition-common`}
                        aria-label="Next slide"
                      >
                        <img
                          src="../../images/swiper-arrowNext-white.svg"
                          width={15}
                          height={15}
                          loading="lazy"
                          alt="Arrow"
                          className="lgscreen:h-[10px] lgscreen:w-[10px]"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
            <div
              className={`lgscreen:w-full w-6/12 ${
                module.imagePosition === "left"
                  ? "order-2 lg:order-1 pl-60 xlscreen:pl-45 lgscreen:pl-0"
                  : "order-2 lg:order-1 pr-60 xlscreen:pr-45 lgscreen:pr-0"
              }`}
            >
              <div className="max-w-[600px]">
                {module?.heading && (
                  <motion.div
                    variants={animFade}
                    className="title title-grey pt-25"
                  >
                    <h3 className="uppercase font-500 tracking-0.04em">
                      {module.heading}
                    </h3>
                  </motion.div>
                )}
                {module?.description && (
                  <motion.div variants={animFade} className="content pt-20">
                    {parse(module.description)}
                  </motion.div>
                )}
                {module?.button && (
                  <motion.div
                    variants={animFade}
                    className="flex flex-wrap gap-40 xlscreen:gap-25 pt-30 md:pt-40"
                  >
                    <Link
                      to={module.button.url}
                      className="btn btn-grey"
                      target={module.button.target}
                    >
                      {module.button.title}
                    </Link>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>
    );
  }
  return null;
};

export default SliderWithContent;

export const PageSliderWithContentFragment = graphql`
  fragment PageSliderWithContentFragment on WpPage_Pagecontent_PageContent_SliderWithContent {
    extraClass
    extraId
    hideSection
    description
    button {
      target
      title
      url
    }
    heading
    imagePosition
    images {
      image {
        altText
        mediaItemUrl
        title
      }
    }
  }
`;